import { useState, useRef, useEffect } from "react";
import { RotateCw, Maximize2, ZoomIn, ZoomOut, Move3d, Play, Sparkles, MousePointer, Footprints, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { useSiteImages } from "../hooks/use-site-images";

const FALLBACK_MODEL_SRC =
  "https://sketchfab.com/3d-models/commercial-kitchen-2014-7b11498a7e2e407aab9c2663a0dc500e";
const EMERGENCY_GLB = "https://raw.githubusercontent.com/mr-akashdesai/kitchen-kreation/master/models/gltf/kitchenFridgeLarge.glb";
const HERO_IMAGE =
  "https://images.pexels.com/photos/2696064/pexels-photo-2696064.jpeg?auto=compress&cs=tinysrgb&w=1600";

const VIEWS = {
  overview: { orbit: "45deg 65deg 4m", fieldOfView: "30deg" },
  walkthrough: { orbit: "0deg 85deg 1.5m", fieldOfView: "60deg" },
};

// Convert a Sketchfab page URL into an embeddable iframe URL.
// Accepts:
//   https://sketchfab.com/3d-models/name-slug-<ID>
//   https://sketchfab.com/models/<ID>
//   https://sketchfab.com/models/<ID>/embed
function toSketchfabEmbed(url) {
  if (!url || typeof url !== "string") return null;
  if (!url.includes("sketchfab.com")) return null;
  // Extract 32-char hex model ID
  const m = url.match(/([a-f0-9]{32})/i);
  if (!m) return null;
  const id = m[1];
  const params = new URLSearchParams({
    autostart: "1",
    ui_theme: "dark",
    ui_infos: "0",
    ui_watermark_link: "0",
    ui_watermark: "0",
    ui_hint: "1",
    transparent: "0",
  });
  return `https://sketchfab.com/models/${id}/embed?${params.toString()}`;
}

export function Kitchen3DViewer({ title = "Explore a real commercial kitchen — live 3D model", subtitle = "Orbit the full layout or walk between equipment in first person. An immersive engineering demo built on a real model." }) {
  const siteImage = useSiteImages();
  const [launched, setLaunched] = useState(false);
  const [mode, setMode] = useState("overview");
  const [autoRotate, setAutoRotate] = useState(true);
  const [modelUrl, setModelUrl] = useState(FALLBACK_MODEL_SRC);
  const viewerRef = useRef(null);

  useEffect(() => {
    api.get("/settings")
      .then((r) => { if (r.data?.kitchen_model_url) setModelUrl(r.data.kitchen_model_url); })
      .catch(() => {});
  }, []);

  const sketchfabEmbed = toSketchfabEmbed(modelUrl);
  const isSketchfab = !!sketchfabEmbed;

  const applyMode = (key) => {
    setMode(key);
    if (!launched) {
      setLaunched(true);
      return;
    }
    if (isSketchfab) return; // camera control not directly available via embed
    const v = VIEWS[key];
    const mv = viewerRef.current;
    if (mv && v) {
      try {
        mv.cameraOrbit = v.orbit;
        mv.fieldOfView = v.fieldOfView;
      } catch (e) {}
    }
  };

  useEffect(() => {
    if (!launched || isSketchfab) return;
    const v = VIEWS[mode];
    const t = setTimeout(() => {
      const mv = viewerRef.current;
      if (mv && v) {
        try {
          mv.cameraOrbit = v.orbit;
          mv.fieldOfView = v.fieldOfView;
        } catch (e) {}
      }
    }, 500);
    return () => clearTimeout(t);
  }, [launched, mode, isSketchfab]);

  const zoom = (delta) => {
    if (isSketchfab) return;
    const mv = viewerRef.current;
    if (!mv || typeof mv.getCameraOrbit !== "function") return;
    try {
      const cur = mv.getCameraOrbit();
      mv.cameraOrbit = `${cur.theta}rad ${cur.phi}rad ${Math.max(1.2, cur.radius + delta)}m`;
    } catch (e) {}
  };

  const openFullscreen = () => {
    const el = viewerRef.current;
    if (el && typeof el.requestFullscreen === "function") {
      try { el.requestFullscreen(); } catch (e) {}
    }
  };

  return (
    <div className="bg-[#0f172a] rounded-2xl overflow-hidden border border-slate-800 shadow-2xl" data-testid="kitchen-3d-viewer">
      <div className="grid lg:grid-cols-[380px_1fr]">
        {/* LEFT PANEL */}
        <div className="p-6 lg:p-8 space-y-6 border-b lg:border-b-0 lg:border-e border-slate-800">
          <div className="flex flex-wrap gap-2">
            <Chip color="sky" dot>INTERACTIVE EXPERIENCE</Chip>
            <Chip color="slate">REAL 3D MODEL</Chip>
          </div>

          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-blue-700/30 text-red-300 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" />
            Interactive 3D Experience
          </div>

          <div>
            <h3 className="font-display text-2xl lg:text-3xl text-white mb-3 leading-tight">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{subtitle}</p>
          </div>

          <div className="space-y-2">
            <ModeCard
              icon={MousePointer}
              active={mode === "overview"}
              onClick={() => applyMode("overview")}
              title="Overview Mode"
              hint="Orbit · Zoom · Inspect"
              testid="mode-overview"
            />
            <ModeCard
              icon={Footprints}
              active={mode === "walkthrough"}
              onClick={() => applyMode("walkthrough")}
              title="Walkthrough Mode"
              hint="First-person tour"
              testid="mode-walkthrough"
            />
          </div>

          {!launched && (
            <button
              onClick={() => setLaunched(true)}
              data-testid="launch-3d-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#ef4444] hover:bg-[#dc2626] text-white font-semibold rounded-full px-5 py-3 shadow-lg transition-colors"
            >
              <Play className="w-4 h-4 fill-white" /> Launch 3D Experience
            </button>
          )}
        </div>

        {/* RIGHT PANEL — Viewer */}
        <div className="relative min-h-[420px] lg:min-h-[560px] bg-slate-950">
          {/* Top badges */}
          <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between p-4 pointer-events-none">
            <div className="flex gap-2 pointer-events-auto">
              <PillBtn active={mode === "overview"} onClick={() => applyMode("overview")} testid="pill-overview">
                <MousePointer className="w-3.5 h-3.5" /> Overview Mode
              </PillBtn>
              <PillBtn active={mode === "walkthrough"} onClick={() => applyMode("walkthrough")} testid="pill-walkthrough">
                <Footprints className="w-3.5 h-3.5" /> Walkthrough Mode
              </PillBtn>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest text-white font-semibold pointer-events-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              LIVE
            </div>
          </div>

          {/* Poster / Launch overlay */}
          {!launched && (
            <div className="absolute inset-0 z-10" data-testid="launch-overlay">
              <img src={siteImage("design_viewer_preview", HERO_IMAGE)} alt="Commercial kitchen preview" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-slate-950/40" />
              <button
                onClick={() => setLaunched(true)}
                data-testid="launch-overlay-btn"
                className="absolute inset-0 flex flex-col items-center justify-center gap-4 group"
              >
                <div className="w-20 h-20 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 text-[#0891b2] fill-[#0891b2] translate-x-0.5" />
                </div>
                <div className="text-center">
                  <div className="font-display text-2xl text-white mb-1">Launch 3D Experience</div>
                  <div className="text-slate-300 text-sm">Nothing loads until you launch</div>
                </div>
                <div className="inline-flex items-center gap-2 bg-[#ef4444] group-hover:bg-[#0891b2] text-white font-semibold rounded-full px-5 py-2.5 mt-2 transition-colors">
                  <Play className="w-4 h-4 fill-white" /> Open interactive demo
                </div>
              </button>
            </div>
          )}

          {/* Model viewer (lazy-mounted) */}
          {launched && isSketchfab && (
            <>
              <iframe
                ref={viewerRef}
                src={sketchfabEmbed}
                title={title}
                allow="autoplay; fullscreen; xr-spatial-tracking"
                allowFullScreen
                style={{ width: "100%", height: "100%", minHeight: "560px", border: 0, background: "#0b1220" }}
                data-testid="sketchfab-iframe"
              />
              <div className="absolute bottom-20 inset-x-0 flex justify-center pointer-events-none">
                <div className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-[11px] text-slate-700 shadow">
                  <Move3d className="w-3.5 h-3.5 text-[#ef4444]" /> Drag to rotate · Scroll to zoom
                </div>
              </div>
              <div className="absolute bottom-4 inset-x-0 z-10 flex items-center justify-center gap-2 px-4">
                <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur rounded-full p-1.5 shadow-lg">
                  <a href={modelUrl} target="_blank" rel="noreferrer" title="Open on Sketchfab"
                     className="w-8 h-8 rounded-full flex items-center justify-center text-slate-700 hover:bg-slate-100"
                     data-testid="ctrl-open-source">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <Ctrl onClick={openFullscreen} title="Fullscreen" testid="ctrl-fullscreen"><Maximize2 className="w-4 h-4" /></Ctrl>
                </div>
              </div>
            </>
          )}

          {launched && !isSketchfab && (
            <>
              <model-viewer
                ref={viewerRef}
                src={modelUrl}
                alt={title}
                camera-controls
                touch-action="pan-y"
                auto-rotate={autoRotate ? true : undefined}
                auto-rotate-delay="1500"
                rotation-per-second="18deg"
                exposure="0.95"
                shadow-intensity="1"
                environment-image="neutral"
                interaction-prompt="none"
                camera-orbit={VIEWS.overview.orbit}
                field-of-view="30deg"
                onError={() => setModelUrl(EMERGENCY_GLB)}
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: "560px",
                  background: "linear-gradient(180deg, #0b1220 0%, #0f172a 100%)",
                  "--poster-color": "transparent",
                }}
              />

              {/* Bottom controls */}
              <div className="absolute bottom-4 inset-x-0 z-10 flex items-center justify-center gap-2 px-4">
                <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur rounded-full p-1.5 shadow-lg">
                  <Ctrl onClick={() => zoom(-0.4)} title="Zoom in" testid="ctrl-zoom-in"><ZoomIn className="w-4 h-4" /></Ctrl>
                  <Ctrl onClick={() => zoom(0.4)} title="Zoom out" testid="ctrl-zoom-out"><ZoomOut className="w-4 h-4" /></Ctrl>
                  <Ctrl onClick={() => setAutoRotate((v) => !v)} title="Auto-rotate" active={autoRotate} testid="ctrl-rotate"><RotateCw className="w-4 h-4" /></Ctrl>
                  <Ctrl onClick={openFullscreen} title="Fullscreen" testid="ctrl-fullscreen"><Maximize2 className="w-4 h-4" /></Ctrl>
                </div>
              </div>

              <div className="absolute bottom-20 inset-x-0 flex justify-center pointer-events-none">
                <div className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-[11px] text-slate-700 shadow">
                  <Move3d className="w-3.5 h-3.5 text-[#ef4444]" /> Drag to rotate · Scroll to zoom
                </div>
              </div>
            </>
          )}

          {/* Bottom-left tag */}
          <div className="absolute bottom-4 start-4 z-10 inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest text-white font-semibold">
            <span className="w-3.5 h-3.5 rounded-sm bg-[#ef4444]/40 border border-[#ef4444] inline-block" />
            GLB · Commercial Kitchen
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ color, dot, children }) {
  const cls = color === "sky"
    ? "bg-cyan-500/15 border-blue-700/30 text-red-300"
    : "bg-slate-800 border-slate-700 text-slate-300";
  return (
    <span className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-[10px] uppercase tracking-widest font-semibold ${cls}`}>
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />}
      {children}
    </span>
  );
}

function ModeCard({ icon: Icon, active, onClick, title, hint, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-start transition-colors ${
        active
          ? "bg-cyan-500/15 border-blue-700/40"
          : "bg-slate-800/40 border-slate-700 hover:bg-slate-800/70 hover:border-slate-600"
      }`}
    >
      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
        active ? "bg-[#ef4444] text-white" : "bg-slate-700 text-slate-300"
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="text-white font-semibold text-sm leading-tight">{title}</div>
        <div className="text-slate-400 text-xs mt-0.5">{hint}</div>
      </div>
    </button>
  );
}

function PillBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
        active
          ? "bg-[#ef4444] text-white border-[#ef4444]"
          : "bg-white/10 backdrop-blur text-white border-white/20 hover:bg-white/20"
      }`}
    >
      {children}
    </button>
  );
}

function Ctrl({ onClick, title, children, active, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
        active ? "bg-[#ef4444] text-white" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}
